<template>
    <div>
        <ul class="toolbar__vert">
            <li v-if="isSaving" class="toolbar__vert-item">
                <button class="toolbar__vert-button" title="Saving">
                    <font-awesome-icon
                        class="toolbar__vert-icon"
                        :icon="['fas', 'spinner']"
                        :spin="true"
                    />
                </button>
            </li>

            <li v-else class="toolbar__vert-item" :class="changedClass">
                <button class="toolbar__vert-button" title="Save" @click="onSave">
                    <font-awesome-icon class="toolbar__vert-icon" :icon="['fal', 'save']" />
                </button>
            </li>

            <li class="toolbar__vert-item">
                <button class="toolbar__vert-button" title="Responsive Mode" @click="onTogglePreview">
                    <font-awesome-icon class="toolbar__vert-icon" :icon="['fal', 'phone-laptop']" />
                </button>

                <collapse
                    :id="uniqid()"
                    v-model="active.preview"
                    role="tabpanel"
                >
                    <ul class="toolbar__vert-list">
                        <li
                            v-for="deviceObj in devices"
                            :key="deviceObj.name"
                            class="toolbar__vert-subitem"
                            :class="{ [STATE_CLASSES.SELECTED]: deviceObj.name === deviceMode.name }"
                        >
                            <button class="toolbar__vert-button" @click="toggleEditorMode(deviceObj)">
                                <font-awesome-icon class="toolbar__vert-icon" :icon="['fal', deviceObj.icon]" />
                            </button>
                        </li>
                    </ul>
                </collapse>
            </li>

            <li class="toolbar__vert-item">
                <button class="toolbar__vert-button" title="Preview" @click="togglePreviewMode()">
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['far', 'eye']"
                    />
                </button>
            </li>

            <li class="toolbar__vert-item" :class="{ [STATE_CLASSES.SELECTED]: !active.visibility }">
                <button
                    :class="['toolbar__vert-button']"
                    title="Toggle visibility of hidden elements and drop zones"
                    @click="onToggleVisibility();"
                >
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['far', 'eye-slash']"
                    />
                </button>
            </li>

            <li v-if="!isComponentScreen" class="toolbar__vert-item">
                <button class="toolbar__vert-button" title="Edit Metadata" @click="showDrawer('metadata-editor')">
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['fal', 'file-code']"
                    />
                </button>
            </li>

            <li v-if="isPageScreen" class="toolbar__vert-item">
                <button class="toolbar__vert-button" title="Edit Taxonomy" @click="openTaxonomyDrawer">
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['fal', 'tag']"
                    />
                </button>
            </li>

            <li class="toolbar__vert-item" :class="{ [STATE_CLASSES.SELECTED]: guidedMode }">
                <button
                    class="toolbar__vert-button"
                    title="Guided Select"
                    @click="onToggleGuidedSelect"
                >
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['fal', 'map-signs']"
                    />
                </button>
            </li>

            <li v-if="isLocking" class="toolbar__vert-item">
                <button class="toolbar__vert-button" title="Locking">
                    <font-awesome-icon
                        class="toolbar__vert-icon"
                        :icon="['fas', 'spinner']"
                        :spin="true"
                    />
                </button>
            </li>

            <li v-if="!isLocking && isPageScreen && pageData" class="toolbar__vert-item">
                <button class="toolbar__vert-button" :title="lockTooltip" @click="onToggleLock">
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['fal', lockIcon]"
                    />
                </button>
            </li>

            <li v-if="isPageScreen && hasWorkflow" class="toolbar__vert-item">
                <VDropdown offset="0">
                    <slot>
                        <button class="toolbar__vert-button" title="Workflow" type="button">
                            <font-awesome-icon
                                class="toolbar__vert-icon toolbar__vert-icon--large"
                                :icon="['fal', 'project-diagram']"
                            />
                        </button>
                    </slot>

                    <template #popper>
                        <workflow-popover />
                    </template>
                </VDropdown>
            </li>

            <li v-if="isPageScreen || (isComponentScreen && isComponentIdExist)" class="toolbar__vert-item z-index-5">
                <button class="toolbar__vert-button" title="Publish" @click="onPublishClick">
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['fal', 'globe-americas']"
                    />
                </button>
                <div class="flex-publish-options">
                    <div class="flex-publish-options__header"><strong>Publishing targets</strong></div>
                    <div class="flex-publish-options_items" />
                    <div class="flex-publish-options__button-row">
                        <button
                            id="flex-publish"
                            class="fc-button"
                            tabindex="0"
                            @click="publishChecked"
                        >
                            Publish
                        </button>
                        <button
                            id="flex-cancel-publish"
                            tabindex="0"
                            class="fc-button-alt"
                            @click="onCloseBox"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </li>

            <li v-if="isPageScreen && pageData" class="toolbar__vert-item">
                <button class="toolbar__vert-button" title="Add Favorite" @click="onFavorite">
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="[favoriteIconSet, 'heart']"
                    />
                </button>
            </li>

            <li
                v-if="!isValid"
                class="toolbar__vert-item is-invalid"
            >
                <button
                    class="toolbar__vert-button"
                    title="Validation Issues"
                    @click="onValidationClick"
                >
                    <font-awesome-icon
                        class="toolbar__vert-icon toolbar__vert-icon--large"
                        :icon="['fal', 'exclamation-triangle']"
                    />
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import uniqid from 'uniqid';

import mapFilters from '@/utils/map-filters';
import { getDevices } from '@/utils/devices';
import { SCREEN_NAMES } from '@/data/constants/screen';
import { SAVE_COMPONENT, SAVE_PAGE, TOGGLE_LOCK, PUBLISH_ITEM, LOAD_SETTINGS } from '@/store/types/action-types';
import {
    CLEAR_COMPONENT_DATA,
    HIDE_SNACKBAR,
    SET_DEVICE_MODE,
    SET_PREVIEW_MODE,
    SHOW_ALERT,
    SHOW_SNACKBAR,
    TOGGLE_FAVORITE,
    TOGGLE_GUIDED_SELECT,
    SHOW_DRAWER,
    SHOW_MODAL,
    SET_SELECTED_ELEMENT,
} from '@/store/types/mutation-types';
import { STATE_CLASSES } from '@/data/constants/classes';
import WorkflowPopover from '@/components/page-editor/shared/workflow-popover';

export default {
    name: 'toolbar-vertical',

    components: { WorkflowPopover },

    props: {
        pageId: {
            type: String,
            default: '',
        },

        screen: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            active: {
                preview: false,
                visibility: true,
            },

            devices: getDevices(),
            isLocking: false,
            isSaving: false,
            responsiveMode: 'desktop',
            STATE_CLASSES,
            uniqid,
        };
    },

    computed: {
        ...mapState({
            addingComponent: state => state.page.addingComponent,
            changedData: state => state.page.changedData,
            deviceMode: state => state.page.deviceMode,
            favorites: state => state.page.favorites,
            guidedMode: state => state.page.guidedMode,
            pageData: state => state.page.pageData,
            selectedComponent: state => state.page.selectedComponent,
            selectedRendering: state => state.page.selectedRendering,
            validationErrors: state => state.page.validationErrors,
            selectedComponentMetadata: state => state.page.selectedComponentMetadata,
        }),

        changedClass() {
            return this.changedData ? STATE_CLASSES.ENABLED : '';
        },

        favoriteIconSet() {
            const obj = this.favorites.find(o => o.id === this.pageData.pageData.id);

            if (obj !== undefined) {
                return 'fas';
            }

            return 'fal';
        },

        lockIcon() {
            if (!this.pageData.pageData.locked) {
                return 'lock-open';
            }

            return 'lock';
        },

        lockTooltip() {
            if (this.pageData && this.pageData.pageData.locked) {
                return `Locked by ${ this.pageData.pageData.lockedby }`;
            }

            return 'Lock this component';
        },

        hasWorkflow() {
            return this.pageData && this.pageData.pageData.workflow.state;
        },

        isComponentScreen() {
            return this.screen === SCREEN_NAMES.COMPONENT_EDITOR;
        },

        isPageScreen() {
            return this.screen === SCREEN_NAMES.PAGE_EDITOR;
        },

        isValid() {
            return !this.validationErrors || this.validationErrors.length < 1;
        },

        isComponentIdExist() {
            return !!this.selectedComponentMetadata?.id;
        },

    },

    watch: {
        'pageData.pageData.locked': function lockWatcher() {
            this.isLocking = false;
        },
    },

    created() {
        this.$keydown(['ctrl', 's'], this.onSave);
        document.body.classList.add('is-hidden');
    },

    beforeDestroy() {
        this.toggleEditorMode(this.devices[0]);
    },

    methods: {
        ...mapFilters(['titleCase']),

        ...mapActions({
            publishItem: PUBLISH_ITEM,
            saveComponent: SAVE_COMPONENT,
            savePage: SAVE_PAGE,
            toggleLock: TOGGLE_LOCK,
            loadSettings: LOAD_SETTINGS,
        }),

        ...mapMutations({
            clearComponentData: CLEAR_COMPONENT_DATA,
            hideSnackbar: HIDE_SNACKBAR,
            setDeviceMode: SET_DEVICE_MODE,
            setPreviewMode: SET_PREVIEW_MODE,
            showAlert: SHOW_ALERT,
            showDrawer: SHOW_DRAWER,
            showModal: SHOW_MODAL,
            showSnackbar: SHOW_SNACKBAR,
            toggleFavorite: TOGGLE_FAVORITE,
            toggleGuidedSelect: TOGGLE_GUIDED_SELECT,
            setSelectedElement: SET_SELECTED_ELEMENT,
        }),

        onFavorite() {
            const newFavorite = { id: this.pageData.pageData.id, name: this.pageData.pageData.itemName };
            this.toggleFavorite(newFavorite);
        },

        onCloseBox() {
            const publishOptionsBox = document.querySelector('.flex-publish-options');
            publishOptionsBox.classList.remove('active');
        },

        publishChecked() {
            // get all publishing target ids which are checked
            const publishTargetArray = [];
            const activePubTargets = document.querySelectorAll('[data-target-id]');
            // publish each
            activePubTargets.forEach(function (item) {
                if (item.checked === true) {
                const currentTargetId = item.getAttribute('data-target-id');
                publishTargetArray.push(currentTargetId);
                }
            });

            const publishOptionsBox = document.querySelector('.flex-publish-options');

            if ( publishTargetArray.length === 0 ) {
                this.showAlert({ description: 'Please choose 1 or more publishing targets.' });
            }
                if (( publishTargetArray.length > 0 ) && (this.pageData !== null)) {
                    this.publishItem({ itemid: this.pageData.pageData.id, language: this.pageData.pageData.language, publishingTargets: publishTargetArray });
                    this.showAlert({ description: 'Page queued for publish.' });
                    publishOptionsBox.classList.remove('active');
                }
                if (( publishTargetArray.length > 0 ) && (this.isComponentIdExist)) {
                    this.publishItem({ itemid: this.selectedComponentMetadata.id, language: 'en', publishingTargets: publishTargetArray });
                    this.showAlert({ description: 'Component queued for publish.' });
                    publishOptionsBox.classList.remove('active');
                }
        },

        basePublish() {
            if (this.pageData !== null) {
                this.publishItem({ itemid: this.pageData.pageData.id, language: this.pageData.pageData.language });
                this.showAlert({ description: 'Page queued for publish.' });
            } else if (this.isComponentIdExist) {
                this.publishItem({ itemid: this.selectedComponentMetadata.id, language: 'en' });
                this.showAlert({ description: 'Component queued for publish.' });
            }
        },

        onPublishClick() {
            const publishingTargetsAvail = localStorage.getItem('isPubTargetAvail');
            const publishingTargetsJson = localStorage.getItem('publishingTargets');
            const publishingTargetsParsed = JSON.parse(publishingTargetsJson);
            const flexPublishItems = document.querySelector('.flex-publish-options_items');

            if (publishingTargetsAvail === 'true') {
                const targetsAdded = document.querySelector('.flex-publish-options__item');
                if ((!targetsAdded)) {
                    const mappedTargets = publishingTargetsParsed.map((target, index) => { return `<div class="flex-publish-options__item" key=${index}><input data-target-id="${target.id}" id="publish-target-${index}" type="checkbox"><label id="publish-target-${index}_label" for="publish-target-${index}">${target.name}</label></div>`; });
                    flexPublishItems.innerHTML = mappedTargets;
                    const updatedVersion = flexPublishItems.innerHTML;
                    const originalString = updatedVersion;
                    const newString = originalString.replaceAll(',', '');
                    flexPublishItems.innerHTML = newString;
                }
                const publishOptionsBox = document.querySelector('.flex-publish-options');
                publishOptionsBox.classList.toggle('active');
            }
            if (publishingTargetsAvail === 'false') {
                this.basePublish();
             }
        },

        onToggleLock() {
            this.isLocking = true;
            this.toggleLock(this.pageData.pageData.id);
        },

        openTaxonomyDrawer() {
            this.showDrawer('taxonomy-editor');
        },

        onToggleGuidedSelect() {
            if (!this.selectedRendering) {
                this.showSnackbar({
                    text: 'Please select a component first!',
                });

                return;
            }

            this.$root.$emit('flex::guided::toggle');
            this.toggleGuidedSelect();
        },

        onToggleVisibility() {
            this.active.visibility = !this.active.visibility;
            document.body.classList.toggle('is-hidden');
        },

        onTogglePreview() {
            this.active.preview = !this.active.preview;
        },

        async onSave() {
            if (!this.changedData) return;

            if (!this.isValid) {
                this.validate();
            }

            // clear component data cache
            this.clearComponentData();

            if (this.isComponentScreen) {
                if (this.addingComponent) {
                    this.showModal('component-modal-save');
                } else {
                    this.isSaving = true;
                    await this.saveComponent({});

                    this.isSaving = false;
                }

                return;
            }

            this.isSaving = true;

            try {
                await this.savePage(this.$route.name);
            } catch (error) {
                this.showAlert({ description: error });
            }

            this.isSaving = false;
        },

        onWorkflowClick() {
            this.active.preview = !this.active.preview;
        },

        toggleEditorMode(deviceMode) {
            this.setDeviceMode(deviceMode);
            this.responsiveMode = deviceMode.displayName;

            if (deviceMode.name !== 'all') {
                this.showSnackbar({
                    title: `${ this.titleCase(this.responsiveMode) } mode enabled.`,
                    text: `Any styles applied will only affect ${ this.responsiveMode } devices.`,
                });
            } else {
                this.hideSnackbar();
            }
        },

        togglePreviewMode() {
            this.setPreviewMode(true);
        },

        isHidden() {
            this.isHidden = true;
        },

        onValidationClick() {
            this.validate();
        },

        validate() {
            const messages = [];
            const $this = this;

            this.validationErrors.forEach((item) => {
                item.messages.forEach((message) => {
                    messages.push(
                        {
                            text: message,
                            onClick() {
                                $this.setSelectedElement({ element: item.element });
                            },
                        },
                    );
                });
            });

            this.showSnackbar({
                title: 'WARNING',
                messages,
                persist: true,
            });
        },
    },
};
</script>

<style lang="scss">
.z-index-5 {
    z-index: 5;
}
.flex-publish-options  {
    display: none;
    &.active {
        display: flex;
        display: -webkit-box;
    }
    position: absolute;
    right: 0;
    background: #fff;
    width: auto;
    min-width: 250px;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    flex-direction: column;
    -webkit-box-align: start;
    align-items: flex-start;
    padding: 12px;
    border: 10px solid #2e2f30;
    margin-top: 1px;
    color: #000;
    &__header {
        margin-bottom: 10px;
    }
    input {
        margin-right: 10px;
        width: 16px;
        height: 16px;
    }
    &__item {
        display: flex;
        align-items: center;
    }
    &__button-row {
        margin-top: 10px;
        display: flex;
        button {
            padding: 10px;
            margin-right: 12px;
            color: #fff;
        }
    }
}
.toolbar {
    &__vert {
        padding: 0;
        margin: 0;
        border-right: 1px solid var(--sidebar-actionMenuBorderColor);
        height: 100vh;
        background-color: var(--sidebar-actionMenuBackgroundColor);
    }

    &__vert-item {
        position: relative;
        list-style: none;
        border-bottom: 1px solid var(--sidebar-actionMenuBorderColor);
        color: var(--sidebar-actionMenuColor);

        &.#{ $is-expanded },
        &.#{ $is-selected } {
            color: var(--form-buttonColor);
            background-color: var(--form-buttonBackgroundColor);
        }

        &.#{ $is-enabled } {
            color: var(--form-buttonColor);
            background-color: $attention-color;
        }

        &.#{ $is-invalid } {
            color: $attention-color !important;
        }
    }

    &__vert-button {
        border: none;
        background-color: transparent;
        width: 100%;
        min-width: 40px;
        height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 18px;
    }

    &__vert-icon--large {
        font-size: 1.25em;
    }

    &__vert-list {
        padding: 0;
        margin: 0;
    }

    &__vert-subitem {
        list-style: none;
        border-top: 1px solid var(--sidebar-actionMenuBorderColor);

        &.#{ $is-selected } {
            color: var(--form-buttonColor);
            background-color: var(--form-buttonBackgroundColor);
        }
    }
}
</style>
